import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, API, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios } from '@umanforge/common';

export type CloudElementType = 'directory' | 'file';

export interface CloudDirFile {
  id: string;
  dirParentId: string | null;
  siteId: string;
  name: string;
  extension: string | null;
  description: string | null;
  path: string;
  type: CloudElementType;
  isBackup: boolean;
  fileSize: number;
  authorId: string | null;
  lastActivity: string;
  children: CloudDirFile[];
}

export interface PostCloudDirBody {
  cloudDirFileId: string;
  name: string;
  description?: string;
}

export interface PutCloudDirFileBody {
  name?: string;
  description?: string;
}

export interface MoveCloudDirFileBody {
  fromCloudDirFileId: string;
  toCloudDirFileId: string;
}

export class CloudService {

  /* GET /api/common/cloud/:siteId — arbre complet des fichiers/dossiers du site */
  static getCloudsFromSite(
    siteId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<CloudDirFile[]> {
    return new Promise( async resolve => {
      const controller = new AbortController();
      const timeout = setTimeout( () => { controller.abort(); }, 30_000 );
      const headers = await CommonGlobalService.getHeaderRequest( token );
      AXIOS.get( `${ API.CLOUD }/${ siteId }`, { ...headers, signal: controller.signal } )
        .then( ( response: AxiosResponse<CloudDirFile[]> ) => {
          clearTimeout( timeout );
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          clearTimeout( timeout );
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( [] );
        } );
    } );
  }

  /* POST /api/common/cloud/directory — créer un répertoire */
  static postCloudDir(
    body: PostCloudDirBody,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ id: string } | null> {
    return new Promise( async resolve => {
      AXIOS.post( `${ API.CLOUD }/directory`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ id: string }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( null );
            return;
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

  /* POST /api/common/cloud/:cloudDirFileId — envoyer des fichiers dans un répertoire */
  static postCloudDirFiles(
    cloudDirFileId: UUID,
    formData: FormData,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      const headers = await CommonGlobalService.getHeaderRequest( token );
      AXIOS.post(
        `${ API.CLOUD }/${ cloudDirFileId }`,
        formData,
        { ...headers, headers: { ...headers.headers } }
      )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( false );
            return;
          }
          resolve( true );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  /* PUT /api/common/cloud/:cloudDirFileId — renommer / modifier la description */
  static putCloudDirFile(
    cloudDirFileId: UUID,
    body: PutCloudDirFileBody,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${ API.CLOUD }/${ cloudDirFileId }`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( false );
            return;
          }
          resolve( true );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  /* PUT /api/common/cloud/move — déplacer un fichier ou dossier */
  static moveCloudDirFile(
    body: MoveCloudDirFileBody,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${ API.CLOUD }/move`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( false );
            return;
          }
          resolve( true );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  /* DELETE /api/common/cloud/:cloudDirFileId — supprimer un fichier ou dossier */
  static deleteCloudDirFile(
    cloudDirFileId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${ API.CLOUD }/${ cloudDirFileId }`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( false );
            return;
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  /* GET /api/common/cloud/download/:cloudDirFileId — télécharger un fichier ou dossier (blob) */
  static downloadCloudDirFile(
    cloudDirFileId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios
  ): Promise<Blob | null> {
    return new Promise( async resolve => {
      const headers = await CommonGlobalService.getHeaderRequest( token );
      AXIOS.get(
        `${ API.CLOUD }/download/${ cloudDirFileId }`,
        { ...headers, responseType: 'blob' }
      )
        .then( ( response: AxiosResponse<Blob> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

}
